// @@TASK_LISTS_v100@@ One to-do list (or the Inbox): groups, inline add,
// cross-off with a completed section, drag to reorder / regroup.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { apiFetch } from '../lib/api';
import { TodoRow, AssigneePicker, setDone, useUndoToast } from '../components/TodoBits';

const JSONH = { 'Content-Type': 'application/json' };
const NONE = '__none';

function AddTodo({ people, onAdd, onClose }) {
  const [f, setF] = useState({ title: '', assignee_ids: [], due_date: '' });
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  async function submit(e) {
    e.preventDefault();
    if (!f.title.trim() || busy) return;
    setBusy(true);
    const ok = await onAdd(f);
    setBusy(false);
    if (ok) { setF({ title: '', assignee_ids: f.assignee_ids, due_date: '' }); ref.current && ref.current.focus(); }
  }
  return (
    <form className="hq-todo-add" onSubmit={submit} onKeyDown={(e) => { if (e.key === 'Escape') onClose(); }}>
      <span className="hq-todo-check hq-todo-check-ghost" aria-hidden="true" />
      <div className="hq-todo-add-body">
        <input ref={ref} className="hq-input" autoFocus placeholder="Describe this to-do…" value={f.title}
          onChange={(e) => setF({ ...f, title: e.target.value })} />
        <div className="hq-todo-add-opts">
          <AssigneePicker compact people={people} value={f.assignee_ids} onChange={(v) => setF({ ...f, assignee_ids: v })} />
          <label className="hq-todo-inline-label">Due
            <input className="hq-input hq-select-sm" type="date" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} />
          </label>
        </div>
        <div className="hq-todo-actions">
          <button className="hq-btn" type="submit" disabled={!f.title.trim() || busy}>Add this to-do</button>
          <button className="hq-btn hq-btn-ghost" type="button" onClick={onClose}>Done adding</button>
        </div>
      </div>
    </form>
  );
}

export default function TaskList() {
  const { listId } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  const [people, setPeople] = useState([]);
  const [allDone, setAllDone] = useState(false);
  const [addingIn, setAddingIn] = useState(null);
  const [crossing, setCrossing] = useState({});
  const [editing, setEditing] = useState(null);       // { name, description }
  const [newGroup, setNewGroup] = useState(null);     // string while adding
  const [renaming, setRenaming] = useState(null);     // { id, name }
  const [drag, setDrag] = useState(null);             // { type: 'task'|'group', id }
  const [over, setOver] = useState(null);             // { kind, id, pos }
  const [toast, showToast] = useUndoToast();

  const load = useCallback(async (all) => {
    const res = await apiFetch(`/api/tasks/lists/${listId}${all ? '?done_limit=all' : ''}`);
    if (res.ok) { setD(await res.json()); setErr(''); }
    else setErr(res.status === 404 ? 'This list doesn’t exist (it may have been deleted).' : 'Could not load this list.');
  }, [listId]);
  useEffect(() => { setD(null); setAllDone(false); load(false); }, [load]);
  useEffect(() => {
    apiFetch('/api/tasks/assignees').then((r) => (r.ok ? r.json() : null)).then((x) => x && setPeople(x.assignees || [])).catch(() => {});
  }, []);
  const reload = () => load(allDone);

  if (err) return <div className="hq-page-content"><div className="hq-page-head"><h1>Tasks</h1><p>{err}</p></div><Link to="/tasks" className="hq-todo-linkbtn">← All lists</Link></div>;
  if (!d) return <div className="hq-page-content"><p className="hq-empty">Loading…</p></div>;

  const { list, groups } = d;
  const isInbox = !!list.inbox;
  const sections = [
    { id: null, key: NONE, name: null, items: d.tasks.filter((t) => !t.group_id) },
    ...groups.map((g) => ({ id: g.id, key: g.id, name: g.name, items: d.tasks.filter((t) => t.group_id === g.id) })),
  ];
  const openCount = d.tasks.length;
  const doneCount = d.completed.filter((t) => t.status === 'done').length + Math.max(0, d.completed_total - d.completed.length);
  const total = openCount + doneCount;

  // ── to-do actions ──
  async function addTodo(sectionId, f) {
    const res = await apiFetch('/api/tasks', { method: 'POST', headers: JSONH,
      body: JSON.stringify({ ...f, list_id: isInbox ? null : list.id, group_id: sectionId }) });
    if (!res.ok) { const x = await res.json().catch(() => ({})); window.alert(x.error || 'Could not add the to-do.'); return false; }
    const x = await res.json();
    setD((cur) => ({ ...cur, tasks: [...cur.tasks, x.task] }));
    return true;
  }
  async function toggle(t) {
    if (t.status === 'done' || t.status === 'cancelled') {
      await setDone(t, false);
      reload();
      return;
    }
    setCrossing((c) => ({ ...c, [t.id]: true }));
    const r = await setDone(t, true);
    setTimeout(() => { setCrossing((c) => { const n = { ...c }; delete n[t.id]; return n; }); reload(); }, 650);
    if (r) showToast(`Completed “${t.title}”${r.spawned ? ' · next one scheduled' : ''}`, async () => { await setDone(t, false); reload(); });
  }

  // ── list / group actions ──
  async function saveList(e) {
    e.preventDefault();
    const res = await apiFetch(`/api/tasks/lists/${list.id}`, { method: 'PATCH', headers: JSONH, body: JSON.stringify(editing) });
    if (res.ok) { setEditing(null); reload(); }
  }
  async function archive(flag) {
    await apiFetch(`/api/tasks/lists/${list.id}`, { method: 'PATCH', headers: JSONH, body: JSON.stringify({ archived: flag }) });
    if (flag) navigate('/tasks'); else reload();
  }
  async function removeList() {
    const n = openCount + d.completed_total;
    if (!window.confirm(`Delete “${list.name}”?${n ? ` Its ${n} to-do${n === 1 ? '' : 's'} will move to the Inbox.` : ''}`)) return;
    const res = await apiFetch(`/api/tasks/lists/${list.id}`, { method: 'DELETE' });
    if (res.ok) navigate('/tasks');
  }
  async function addGroup(e) {
    e.preventDefault();
    if (!newGroup || !newGroup.trim()) return;
    const res = await apiFetch(`/api/tasks/lists/${list.id}/groups`, { method: 'POST', headers: JSONH, body: JSON.stringify({ name: newGroup }) });
    if (res.ok) { setNewGroup(null); reload(); }
  }
  async function renameGroup(e) {
    e.preventDefault();
    const res = await apiFetch(`/api/tasks/groups/${renaming.id}`, { method: 'PATCH', headers: JSONH, body: JSON.stringify({ name: renaming.name }) });
    if (res.ok) { setRenaming(null); reload(); }
  }
  async function deleteGroup(g) {
    if (!window.confirm(`Delete the group “${g.name}”? Its to-dos stay in this list.`)) return;
    await apiFetch(`/api/tasks/groups/${g.id}`, { method: 'DELETE' });
    reload();
  }

  // ── drag & drop ──
  async function persist(newGroups, newTasks) {
    setD((cur) => ({ ...cur, groups: newGroups, tasks: newTasks }));
    const order = [null, ...newGroups.map((g) => g.id)];
    const items = [];
    for (const gid of order) for (const t of newTasks) if ((t.group_id || null) === gid) items.push({ id: t.id, group_id: gid });
    await apiFetch(`/api/tasks/lists/${list.id}/layout`, { method: 'POST', headers: JSONH,
      body: JSON.stringify({ groups: newGroups.map((g) => g.id), items }) });
  }
  function dropTask(targetGroup, beforeId, after) {
    const moving = d.tasks.find((t) => t.id === drag.id);
    if (!moving) return;
    const secs = sections.map((s) => ({ id: s.id, items: s.items.filter((t) => t.id !== moving.id) }));
    const sec = secs.find((s) => s.id === targetGroup);
    let idx = sec.items.length;
    if (beforeId) { idx = sec.items.findIndex((t) => t.id === beforeId); if (idx < 0) idx = sec.items.length; else if (after) idx += 1; }
    sec.items.splice(idx, 0, { ...moving, group_id: targetGroup });
    const flat = [];
    secs.forEach((s) => s.items.forEach((t, i) => flat.push({ ...t, group_id: s.id, position: i + 1 })));
    persist(groups, flat);
  }
  function dropGroup(targetId, after) {
    const ids = groups.map((g) => g.id).filter((id) => id !== drag.id);
    let at = ids.indexOf(targetId); if (at < 0) at = ids.length; else if (after) at += 1;
    ids.splice(at, 0, drag.id);
    const byId = Object.fromEntries(groups.map((g) => [g.id, g]));
    persist(ids.map((id) => byId[id]), d.tasks);
  }
  const endDrag = () => { setDrag(null); setOver(null); };
  const half = (e) => { const r = e.currentTarget.getBoundingClientRect(); return (e.clientY - r.top) > r.height / 2 ? 'after' : 'before'; };

  function rowDrag(t, sec) {
    return {
      over: over && over.kind === 'task' && over.id === t.id && drag && drag.id !== t.id ? over.pos : null,
      onDragStart: (e) => { e.stopPropagation(); setDrag({ type: 'task', id: t.id }); e.dataTransfer.effectAllowed = 'move'; try { e.dataTransfer.setData('text/plain', t.id); } catch (_) { /* */ } },
      onDragEnd: endDrag,
      onDragOver: (e) => {
        if (!drag || drag.type !== 'task') return;
        e.preventDefault(); e.stopPropagation();
        const pos = half(e);
        if (!over || over.id !== t.id || over.pos !== pos) setOver({ kind: 'task', id: t.id, pos });
      },
      onDrop: (e) => { e.preventDefault(); e.stopPropagation(); if (drag && drag.type === 'task' && drag.id !== t.id) dropTask(sec.id, t.id, over && over.pos === 'after'); endDrag(); },
    };
  }
  function sectionDrop(sec) {
    return {
      onDragOver: (e) => {
        if (!drag || drag.type !== 'task') return;
        e.preventDefault();
        if (!over || over.kind !== 'section' || over.id !== sec.key) setOver({ kind: 'section', id: sec.key });
      },
      onDrop: (e) => { e.preventDefault(); if (drag && drag.type === 'task') dropTask(sec.id, null); endDrag(); },
    };
  }
  function groupHeadDrag(g) {
    return {
      draggable: true,
      onDragStart: (e) => { setDrag({ type: 'group', id: g.id }); e.dataTransfer.effectAllowed = 'move'; try { e.dataTransfer.setData('text/plain', g.id); } catch (_) { /* */ } },
      onDragEnd: endDrag,
      onDragOver: (e) => {
        if (!drag) return;
        e.preventDefault(); e.stopPropagation();
        if (drag.type === 'group') { const pos = half(e); if (!over || over.id !== g.id || over.pos !== pos) setOver({ kind: 'group', id: g.id, pos }); }
        else if (!over || over.kind !== 'section' || over.id !== g.id) setOver({ kind: 'section', id: g.id });
      },
      onDrop: (e) => {
        e.preventDefault(); e.stopPropagation();
        if (drag && drag.type === 'group' && drag.id !== g.id) dropGroup(g.id, over && over.pos === 'after');
        else if (drag && drag.type === 'task') { const first = d.tasks.find((t) => t.group_id === g.id); dropTask(g.id, first ? first.id : null, false); }
        endDrag();
      },
    };
  }

  return (
    <div className="hq-page-content hq-todo-page">
      <div className="hq-todo-crumb"><Link to="/tasks">Tasks</Link> <span>›</span> {list.name}</div>

      <div className="hq-panel hq-todo-listpanel">
        {editing ? (
          <form className="hq-todo-newlist hq-todo-editlist" onSubmit={saveList}>
            <input className="hq-input" autoFocus value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
            <textarea className="hq-input hq-todo-textarea" rows={2} placeholder="Description (optional)" value={editing.description}
              onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
            <div className="hq-todo-actions">
              <button className="hq-btn" type="submit" disabled={!editing.name.trim()}>Save changes</button>
              <button className="hq-btn hq-btn-ghost" type="button" onClick={() => setEditing(null)}>Cancel</button>
            </div>
          </form>
        ) : (
          <div className="hq-todo-listhead">
            <div className="hq-todo-listhead-main">
              <h1>{isInbox && <span aria-hidden="true">📥 </span>}{list.name}</h1>
              {list.archived && <span className="hq-pill hq-tstatus-cancelled">Archived</span>}
              {list.description && <p>{list.description}</p>}
              <div className="hq-todo-progress hq-todo-progress-lg">
                <div className="hq-todo-bar"><span style={{ width: (total ? Math.round(doneCount / total * 100) : 0) + '%' }} /></div>
                <small>{total ? `${doneCount}/${total} completed` : 'No to-dos yet'}</small>
              </div>
            </div>
            {!isInbox && (
              <div className="hq-todo-listtools">
                <button className="hq-btn hq-btn-ghost" onClick={() => setNewGroup('')}>Add group</button>
                <button className="hq-btn hq-btn-ghost" onClick={() => setEditing({ name: list.name, description: list.description || '' })}>Edit</button>
                <button className="hq-btn hq-btn-ghost" onClick={() => archive(!list.archived)}>{list.archived ? 'Unarchive' : 'Archive'}</button>
                <button className="hq-link-danger" onClick={removeList}>Delete</button>
              </div>
            )}
          </div>
        )}

        <div className="hq-todo-sections">
          {sections.map((sec) => {
            const g = sec.id ? groups.find((x) => x.id === sec.id) : null;
            const headOver = over && ((over.kind === 'group' && over.id === sec.id) ? ' is-drop-' + over.pos : (over.kind === 'section' && over.id === sec.key ? ' is-drop-into' : ''));
            if (!g && !sec.items.length && groups.length && addingIn !== NONE) {
              // empty ungrouped area above groups: keep a slim drop target + add link
              return (
                <section key={sec.key} className={'hq-todo-section hq-todo-section-slim' + (headOver || '')} {...sectionDrop(sec)}>
                  <button className="hq-todo-addlink" onClick={() => setAddingIn(NONE)}>+ Add a to-do</button>
                </section>
              );
            }
            return (
              <section key={sec.key} className={'hq-todo-section' + (g ? ' is-group' : '') + (!g ? (headOver || '') : '')} {...(!g ? sectionDrop(sec) : {})}>
                {g && (
                  <div className={'hq-todo-grouphead' + (headOver || '')} {...groupHeadDrag(g)}>
                    {renaming && renaming.id === g.id ? (
                      <form onSubmit={renameGroup} className="hq-todo-inlineform">
                        <input className="hq-input hq-select-sm" autoFocus value={renaming.name} onChange={(e) => setRenaming({ ...renaming, name: e.target.value })}
                          onKeyDown={(e) => { if (e.key === 'Escape') setRenaming(null); }} />
                        <button className="hq-btn" type="submit">Save</button>
                      </form>
                    ) : (
                      <>
                        <span className="hq-todo-grip" aria-hidden="true">⋮⋮</span>
                        <h4 className="hq-todo-group-name">{g.name}</h4>
                        <span className="hq-todo-groupcount">{sec.items.length}</span>
                        <span className="hq-todo-groupacts">
                          <button className="hq-todo-linkbtn" onClick={() => setRenaming({ id: g.id, name: g.name })}>Rename</button>
                          <button className="hq-todo-linkbtn hq-todo-linkbtn-danger" onClick={() => deleteGroup(g)}>Delete</button>
                        </span>
                      </>
                    )}
                  </div>
                )}
                <div className="hq-todo-items" {...(g ? sectionDrop(sec) : {})}>
                  {sec.items.map((t) => (
                    <TodoRow key={t.id} task={t} crossing={!!crossing[t.id]} onToggle={() => toggle(t)} drag={rowDrag(t, sec)} />
                  ))}
                  {!sec.items.length && !g && !groups.length && addingIn !== NONE && (
                    <p className="hq-todo-empty">{isInbox ? 'Inbox zero. New and auto-created to-dos show up here.' : 'No to-dos yet. Add the first one.'}</p>
                  )}
                  {!sec.items.length && g && addingIn !== sec.key && <p className="hq-todo-empty hq-todo-empty-sm">Drag to-dos here or add one.</p>}
                  {addingIn === sec.key ? (
                    <AddTodo people={people} onAdd={(f) => addTodo(sec.id, f)} onClose={() => setAddingIn(null)} />
                  ) : (
                    <button className="hq-todo-addlink" onClick={() => setAddingIn(sec.key)}>+ Add a to-do</button>
                  )}
                </div>
              </section>
            );
          })}

          {newGroup !== null && (
            <form className="hq-todo-section is-group hq-todo-inlineform hq-todo-newgroup" onSubmit={addGroup}>
              <input className="hq-input" autoFocus placeholder="Name this group… e.g. Pre-show" value={newGroup}
                onChange={(e) => setNewGroup(e.target.value)} onKeyDown={(e) => { if (e.key === 'Escape') setNewGroup(null); }} />
              <button className="hq-btn" type="submit" disabled={!newGroup.trim()}>Add group</button>
              <button className="hq-btn hq-btn-ghost" type="button" onClick={() => setNewGroup(null)}>Cancel</button>
            </form>
          )}
        </div>

        {d.completed_total > 0 && (
          <div className="hq-todo-completed">
            <h4>Completed <span className="hq-todo-groupcount">{d.completed_total}</span></h4>
            {d.completed.map((t) => (
              <TodoRow key={t.id} task={t} onToggle={() => toggle(t)} completedLine />
            ))}
            {d.completed.length < d.completed_total && (
              <button className="hq-todo-linkbtn" onClick={() => { setAllDone(true); load(true); }}>
                Show all {d.completed_total} completed to-dos
              </button>
            )}
          </div>
        )}
      </div>
      <p className="hq-todo-hint">Tip: drag the ⋮⋮ handle to reorder or move to-dos between groups. To move one to another list, open it.</p>
      {toast}
    </div>
  );
}
